import {Autocomplete, Checkbox, Chip, TextField} from '@mui/material';
import {
  CalendarIcon,
  CheckedCheckboxIcon,
  CloseIcon,
  SelectPopupIcon,
  UncheckedCheckboxIcon,
} from 'assets/icons';
import {calendarStyles, chipStyles, searchAndSelect} from 'styles/MUIStyles/common';

import {DATE_PRESETS} from '../utils';
import {DatePicker} from '@mui/x-date-pickers';
import {Dayjs} from 'dayjs';
import {FC} from 'react';
import {IGetBranchesListItem} from 'store/models/branches';
import {StationsListItem} from 'store/models/stations';
import styles from '../Dashboard.module.scss';

interface Props {
  startDate: Dayjs | null;
  endDate: Dayjs | null;
  onStartDateChange: (date: Dayjs | null) => void;
  onEndDateChange: (date: Dayjs | null) => void;
  branchOptions: IGetBranchesListItem[];
  selectedBranches: IGetBranchesListItem[];
  onBranchesChange: (branches: IGetBranchesListItem[]) => void;
  stationOptions: StationsListItem[];
  selectedStations: StationsListItem[];
  onStationsChange: (stations: StationsListItem[]) => void;
  showingLabel: string;
  onPresetSelect: (range: {start: Dayjs; end: Dayjs}) => void;
  maxRangeYears: number;
}

const dateFieldSx = {...calendarStyles, width: 170};

const DashboardFilters: FC<Props> = (props) => {
  const {startDate, endDate, maxRangeYears} = props;

  // Календари сами не дают выбрать диапазон длиннее лимита (#70).
  const startMinDate = endDate
    ? endDate.subtract(maxRangeYears, 'year')
    : undefined;
  const endMaxDate = startDate
    ? startDate.add(maxRangeYears, 'year')
    : undefined;

  const isPresetActive = (range: {start: Dayjs; end: Dayjs}) =>
    Boolean(
      startDate &&
        endDate &&
        startDate.isSame(range.start, 'day') &&
        endDate.isSame(range.end, 'day'),
    );

  return (
    <div className={styles.filtersBar}>
      <DatePicker
        inputFormat="DD.MM.YYYY"
        value={props.startDate}
        minDate={startMinDate}
        maxDate={props.endDate ?? undefined}
        components={{OpenPickerIcon: CalendarIcon}}
        componentsProps={{
          actionBar: {
            actions: ['clear'],
            style: {justifyContent: 'flex-end'},
          },
        }}
        onChange={(newValue) => props.onStartDateChange(newValue)}
        renderInput={(params) => (
          <TextField
            {...params}
            size="small"
            sx={dateFieldSx}
            inputProps={{
              ...params.inputProps,
              placeholder: 'Start date',
              autoComplete: 'off',
            }}
          />
        )}
      />

      <DatePicker
        inputFormat="DD.MM.YYYY"
        value={props.endDate}
        minDate={props.startDate ?? undefined}
        maxDate={endMaxDate}
        components={{OpenPickerIcon: CalendarIcon}}
        componentsProps={{
          actionBar: {
            actions: ['clear'],
            style: {justifyContent: 'flex-end'},
          },
        }}
        onChange={(newValue) => props.onEndDateChange(newValue)}
        renderInput={(params) => (
          <TextField
            {...params}
            size="small"
            sx={dateFieldSx}
            inputProps={{
              ...params.inputProps,
              placeholder: 'End date',
              autoComplete: 'off',
            }}
          />
        )}
      />

      <Autocomplete
        multiple
        disableCloseOnSelect
        limitTags={1}
        className={styles.filterField}
        options={props.branchOptions}
        value={props.selectedBranches}
        onChange={(_, value) => props.onBranchesChange(value)}
        getOptionLabel={(option) => option.name}
        isOptionEqualToValue={(option, value) => option.id === value.id}
        popupIcon={<SelectPopupIcon />}
        ListboxProps={{style: {maxHeight: 300}}}
        renderTags={(tagValue, getTagProps) =>
          tagValue.map((option, index) => (
            <Chip
              {...getTagProps({index})}
              key={option.id}
              size="small"
              label={option.name}
              sx={chipStyles}
              deleteIcon={<CloseIcon style={{width: '20px', height: '20px'}} />}
            />
          ))
        }
        renderOption={(optionProps, option, {selected}) => (
          <li {...optionProps} key={option.id}>
            <Checkbox
              icon={<UncheckedCheckboxIcon />}
              checkedIcon={<CheckedCheckboxIcon />}
              style={{marginRight: 8}}
              checked={selected}
            />
            {option.name}
          </li>
        )}
        renderInput={(params) => (
          <TextField
            {...params}
            size="small"
            placeholder="All branches"
            sx={searchAndSelect}
          />
        )}
      />

      <Autocomplete
        multiple
        disableCloseOnSelect
        limitTags={1}
        className={styles.filterField}
        options={props.stationOptions}
        value={props.selectedStations}
        onChange={(_, value) => props.onStationsChange(value)}
        getOptionLabel={(option) =>
          `${option.name}/${option.posChassicNumber}`
        }
        isOptionEqualToValue={(option, value) => option.id === value.id}
        popupIcon={<SelectPopupIcon />}
        ListboxProps={{style: {maxHeight: 300}}}
        renderTags={(tagValue, getTagProps) =>
          tagValue.map((option, index) => (
            <Chip
              {...getTagProps({index})}
              key={option.id}
              size="small"
              label={option.name}
              sx={chipStyles}
              deleteIcon={<CloseIcon style={{width: '20px', height: '20px'}} />}
            />
          ))
        }
        renderOption={(optionProps, option, {selected}) => (
          <li {...optionProps} key={option.id}>
            <Checkbox
              icon={<UncheckedCheckboxIcon />}
              checkedIcon={<CheckedCheckboxIcon />}
              style={{marginRight: 8}}
              checked={selected}
            />
            {option.name}/{option.posChassicNumber}
          </li>
        )}
        renderInput={(params) => (
          <TextField
            {...params}
            size="small"
            placeholder="All stations"
            sx={searchAndSelect}
          />
        )}
      />

      <span className={styles.showing}>{props.showingLabel}</span>

      <div className={styles.presets}>
        {DATE_PRESETS.map((preset) => {
          const range = preset.getRange();
          return (
            <button
              key={preset.label}
              type="button"
              className={`${styles.presetChip} ${
                isPresetActive(range) ? styles.presetChipActive : ''
              }`}
              onClick={() => props.onPresetSelect(range)}
            >
              {preset.label}
            </button>
          );
        })}
      </div>
    </div>
  );
};

export default DashboardFilters;
